import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { toast } from "../components/AppToast";
import * as courierAPI from "../services/courier/courier";
import * as courierServiceAPI from "../services/courier/courierService";
import * as rateCardAPI from "../services/courier/rateCardService";
import * as rateSlabAPI from "../services/courier/rateSlabService";

const CourierContext = createContext(null);

let nextId = 100;
const uid = () => `gen_${nextId++}`;

export const CourierProvider = ({ children }) => {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCouriers = useCallback(async () => {
    setLoading(true);
    try {
      const [courierData, serviceData, rateCardData, slabData] = await Promise.all([
        courierAPI.getCourierList(),
        courierServiceAPI.getAllCourierServices(),
        rateCardAPI.getAllRateCards(),
        rateSlabAPI.getAllRateSlabs(),
      ]);
      const courierList = Array.isArray(courierData) ? courierData : [];
      const serviceList = Array.isArray(serviceData) ? serviceData : [];
      const rcList = Array.isArray(rateCardData) ? rateCardData : [];
      const slabList = Array.isArray(slabData) ? slabData : [];

      const slabsByRateCard = {};
      slabList.forEach((sl) => {
        const rcId = typeof sl.rateCardId === "object" ? sl.rateCardId?._id : sl.rateCardId;
        if (!rcId) return;
        if (!slabsByRateCard[rcId]) slabsByRateCard[rcId] = [];
        slabsByRateCard[rcId].push({
          id: sl._id,
          fromWeight: sl.minWeight,
          toWeight: sl.maxWeight,
          ratePerKg: sl.baseRate,
          minCharge: sl.minCharge,
          additionalPerKg: sl.additionalPerKg,
          roundingType: sl.roundingType,
        });
      });

      const rateCardsByService = {};
      rcList.forEach((rc) => {
        const sId = typeof rc.courierServiceId === "object" ? rc.courierServiceId._id : rc.courierServiceId;
        if (!rateCardsByService[sId]) rateCardsByService[sId] = [];
        rateCardsByService[sId].push({
          id: rc._id,
          name: rc.rateCardName,
          zone: rc.zone,
          effectiveFrom: rc.effectiveFrom?.split("T")[0] ?? "",
          effectiveTo: rc.effectiveTo?.split("T")[0] ?? "",
          status: rc.status,
          slabs: slabsByRateCard[rc._id] ?? [],
        });
      });

      const servicesByCourier = {};
      serviceList.forEach((s) => {
        const cId = typeof s.courierId === "object" ? s.courierId._id : s.courierId;
        if (!servicesByCourier[cId]) servicesByCourier[cId] = [];
        servicesByCourier[cId].push({
          id: s._id,
          name: s.serviceLevel,
          code: s.code,
          mode: s.mode,
          deliveryDays: s.deliveryDays,
          status: s.status,
          rateCards: rateCardsByService[s._id] ?? [],
        });
      });

      setCouriers(courierList.map((c) => ({
        id: c._id,
        name: c.courierName,
        code: c.courierCode,
        contactPerson: c.contactPersonName,
        phone: c.contactPersonNumber,
        trackingUrl: c.trackingUrl,
        status: c.status,
        services: servicesByCourier[c._id] ?? [],
      })));
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to fetch couriers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCouriers();
  }, [fetchCouriers]);

  const toApiPayload = (data) => ({
    courierName: data.name,
    courierCode: data.code,
    contactPersonName: data.contactPerson,
    contactPersonNumber: data.phone,
    status: data.status,
  });

  const addCourier = useCallback(async (data) => {
    try {
      await courierAPI.createCourier(toApiPayload(data));
      toast.success("Courier created successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to create courier");
    }
  }, [fetchCouriers]);

  const updateCourier = useCallback(async (id, data) => {
    try {
      await courierAPI.updateCourier(id, toApiPayload(data));
      toast.success("Courier updated successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to update courier");
    }
  }, [fetchCouriers]);

  const deleteCourier = useCallback(async (id) => {
    try {
      await courierAPI.deleteCourier(id);
      toast.success("Courier deleted successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to delete courier");
    }
  }, [fetchCouriers]);

  const toServicePayload = (data, courierId) => ({
    courierId,
    serviceLevel: data.name,
    code: data.code,
    mode: data.mode,
    deliveryDays: data.deliveryDays,
  });

  const addService = useCallback(async (courierId, data) => {
    try {
      await courierServiceAPI.createCourierService(toServicePayload(data, courierId));
      toast.success("Service created successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to create service");
    }
  }, [fetchCouriers]);

  const updateService = useCallback(async (courierId, serviceId, data) => {
    try {
      await courierServiceAPI.updateCourierService(serviceId, toServicePayload(data, courierId));
      toast.success("Service updated successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to update service");
    }
  }, [fetchCouriers]);

  const deleteService = useCallback(async (courierId, serviceId) => {
    try {
      await courierServiceAPI.deleteCourierService(serviceId);
      toast.success("Service deleted successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to delete service");
    }
  }, [fetchCouriers]);

  const fetchServicesByCourier = useCallback(async (courierId) => {
    try {
      return await courierServiceAPI.getCourierServicesByCourier(courierId);
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to fetch services");
      return [];
    }
  }, []);

  const toRateCardPayload = (data, courierServiceId) => ({
    courierServiceId,
    rateCardName: data.name,
    zone: data.zone,
    effectiveFrom: data.effectiveFrom,
  });

  const addRateCard = useCallback(async (courierId, serviceId, data) => {
    try {
      await rateCardAPI.createRateCard(toRateCardPayload(data, serviceId));
      toast.success("Rate card created successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to create rate card");
    }
  }, [fetchCouriers]);

  const updateRateCard = useCallback(async (courierId, serviceId, rateCardId, data) => {
    try {
      await rateCardAPI.updateRateCard(rateCardId, toRateCardPayload(data, serviceId));
      toast.success("Rate card updated successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to update rate card");
    }
  }, [fetchCouriers]);

  const deleteRateCard = useCallback(async (courierId, serviceId, rateCardId) => {
    try {
      await rateCardAPI.deleteRateCard(rateCardId);
      toast.success("Rate card deleted successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to delete rate card");
    }
  }, [fetchCouriers]);

  const toSlabPayload = (data, rateCardId) => ({
    rateCardId,
    minWeight: data.fromWeight,
    maxWeight: data.toWeight,
    baseRate: data.ratePerKg,
    minCharge: data.minCharge,
  });

  const addSlab = useCallback(async (courierId, serviceId, rateCardId, data) => {
    try {
      await rateSlabAPI.createRateSlab(toSlabPayload(data, rateCardId));
      toast.success("Slab created successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to create slab");
    }
  }, [fetchCouriers]);

  const updateSlab = useCallback(async (courierId, serviceId, rateCardId, slabId, data) => {
    try {
      await rateSlabAPI.updateRateSlab(slabId, toSlabPayload(data, rateCardId));
      toast.success("Slab updated successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to update slab");
    }
  }, [fetchCouriers]);

  const deleteSlab = useCallback(async (courierId, serviceId, rateCardId, slabId) => {
    try {
      await rateSlabAPI.deleteRateSlab(slabId);
      toast.success("Slab deleted successfully");
      await fetchCouriers();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to delete slab");
    }
  }, [fetchCouriers]);

  const value = {
    couriers,
    loading,
    fetchCouriers,
    addCourier,
    updateCourier,
    deleteCourier,
    addService,
    updateService,
    deleteService,
    fetchServicesByCourier,
    addRateCard,
    updateRateCard,
    deleteRateCard,
    addSlab,
    updateSlab,
    deleteSlab,
  };

  return (
    <CourierContext.Provider value={value}>
      {children}
    </CourierContext.Provider>
  );
};

export const useCouriers = () => {
  const ctx = useContext(CourierContext);
  if (!ctx) throw new Error("useCouriers must be used within a CourierProvider");
  return ctx;
};
